// src/Home.js
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import Menu from '../menu';
import { getTasks, createTask, createTaskWithFiles, updateTask, updateTaskWithFiles, getReportingServices, markTasksViewed } from './taskApi';
import { formatBadgeCount } from '../../utils/badgeCounts';
import TaskItem from './TaskItem';
import { useSelector, useDispatch } from 'react-redux';

import DateTimePicker from 'react-datetime-picker'
import DatePicker from 'react-date-picker';
import TimePicker from 'react-time-picker';

import { selectName, selectUserId, selectCurrentUser, selectUsers, setIsLogOut } from '../login/loginSlice';
import { resolveBusinessAccountId, resolveCompanyOwnerUserId } from '../../utils/accountUtils';
import './task.css'
import '../../static/css/main.css'
import { addIcon, closeIcon, closeIconTask, newTask, fileSelect, arrowLIcon } from '../../static/svgs';
import FileMessage from '../documents/FileMessage';
import CompanyPickerList from '../shared/CompanyPickerList';
import { useSectionCompanyPicker } from '../../hooks/useSectionCompanyPicker';
import { useSectionMenuReset } from '../../context/SectionMenuResetContext';
import { useAccountBadgesContext, usePickerBadgeMap } from '../../context/AccountBadgesContext';
import SuspendHoverHint from '../shared/SuspendHoverHint';
import { useBuhServiceSuspended } from '../../hooks/useBuhServiceSuspended';
import '../shared/companyPicker.css';
import 'react-date-picker/dist/DatePicker.css';
import 'react-calendar/dist/Calendar.css';
import 'react-time-picker/dist/TimePicker.css';
import 'react-clock/dist/Clock.css';
import { selectUserName } from '../chat/chatSlice';
import { useNavigate } from 'react-router-dom';
import { url } from '../../static/env';
import { buildAttachmentPreview } from '../chat/chatAttachmentPreview';

const MAX_TASK_FILES = 10;

function taskBelongsToSelectedCompany(task, companyId, legacyUserId) {
    const selected = Number(companyId) || 0;
    if (!selected) return true;
    const taskCompany = Number(task?.company_id) || 0;
    if (taskCompany > 0) return taskCompany === selected;
    const taskUser = Number(task?.user_id) || 0;
    const legacy = Number(legacyUserId) || 0;
    return legacy > 0 && taskUser === legacy;
}

function Tasks({ setIsLoggedIn }) {
    const dispatch = useDispatch()
    const navigate = useNavigate();
    const [tasks, setTasks] = useState([]);
    const [isModalVisible, setModalVisible] = useState(false);
    const [newTaskText, setNewTaskText] = useState('');
    const [newTaskTitle, setNewTaskTitle] = useState('');
    const [selectedFilter, setSelectedFilter] = useState('inProgress')
    const user_id = useSelector(selectUserId)
    const users = useSelector(selectUsers)
    const currentUser = useSelector(selectCurrentUser)
    const name = useSelector(selectName)
    const {
        multiple,
        pickerOpen,
        selectAccount,
        openPicker,
        pickerUsers,
        loading: accessLoading,
        loadError: accessLoadError,
        noAccess,
        isEmployee,
        businessAccountId,
    } = useSectionCompanyPicker('tasks');
    const pickerBadgeByUserId = usePickerBadgeMap('tasks', pickerUsers);
    const { refresh: refreshAccountBadges, tasksByCompanyId, totals } = useAccountBadgesContext();

    /** Владелец задачи = владелец компании; company_id — выбранная компания. */
    const taskOwnerUserId = useMemo(
        () =>
            resolveCompanyOwnerUserId({
                users,
                currentUser,
                userId: user_id,
                businessAccountId,
            }),
        [businessAccountId, users, currentUser, user_id]
    );

    const taskCompanyId = useMemo(() => {
        const cid = Number(currentUser?.company_id);
        if (cid > 0) return cid;
        return null;
    }, [currentUser]);
    const isServiceSuspended = useBuhServiceSuspended(taskOwnerUserId || user_id, taskCompanyId);

    const applyCompanyTasks = useCallback((data) => {
        const list = Array.isArray(data) ? data : [];
        setTasks(list.filter((task) =>
            taskBelongsToSelectedCompany(task, taskCompanyId, currentUser?.legacy_user_id)
        ));
    }, [taskCompanyId, currentUser?.legacy_user_id]);

    useSectionMenuReset('/tasks', () => {
        if (multiple && !pickerOpen) {
            openPicker();
            return true;
        }
        return false;
    });

    const [date, setDate] = useState(() => {
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        return tomorrow;
    });
    const [time, setTime] = useState(() => {
        const now = new Date();
        const hours = String(now.getHours()).padStart(2, '0');
        const minutes = String(now.getMinutes()).padStart(2, '0');
        return `${hours}:${minutes}`;
    });

    const [mode, setMode] = useState('date');
    const [show, setShow] = useState(false);


    const [selectedTask, setSelectedTask] = useState(null);
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [uploadPreviews, setUploadPreviews] = useState([]);
    const [fileActionModal, setFileActionModal] = useState(null);
    const [existingTaskFiles, setExistingTaskFiles] = useState([]);
    const [isTaskReport, setIsTaskReport] = useState(false);
    const [reportingServices, setReportingServices] = useState([]);
    const [selectedReportingServices, setSelectedReportingServices] = useState({});
    const [anotherService, setAnotherService] = useState('');
    const [anotherServiceChecked, setAnotherServiceChecked] = useState({ need_do: false, need_submit: false });
    const [isCreatingTask, setIsCreatingTask] = useState(false);
    const [isSearchMode, setIsSearchMode] = useState(false);
    const [taskSearchQuery, setTaskSearchQuery] = useState('');
    const [searchMatchIndices, setSearchMatchIndices] = useState([]);
    const [activeSearchMatchIdx, setActiveSearchMatchIdx] = useState(0);
    const [taskContextMenu, setTaskContextMenu] = useState(null);

    const isTaskVisibleByFilter = (task) =>
        (selectedFilter === 'inProgress' && !task.finish_date) ||
        (selectedFilter === 'ready' && task.finish_date);

    const buildTaskSearchHaystack = (task) => {
        const parts = [
            String(task.id ?? ''),
            String(task.title ?? ''),
            String(task.text ?? ''),
            String(task.comment ?? ''),
        ];
        (task.files || []).forEach((f) => {
            const raw = f?.path || f?.name || f?.filename || '';
            if (raw) parts.push(String(raw));
        });
        (task.reports || []).forEach((r) => {
            parts.push(String(r?.reporting_service_name ?? ''));
            if (r?.another_service) parts.push(String(r.another_service));
        });
        return parts.join(' ').toLowerCase();
    };

    const formatLocalDateTime = (d) => {
        const dt = new Date(d);
        const yyyy = dt.getFullYear();
        const mm = String(dt.getMonth() + 1).padStart(2, '0');
        const dd = String(dt.getDate()).padStart(2, '0');
        const hh = String(dt.getHours()).padStart(2, '0');
        const mi = String(dt.getMinutes()).padStart(2, '0');
        const ss = String(dt.getSeconds()).padStart(2, '0');
        return `${yyyy}-${mm}-${dd}T${hh}:${mi}:${ss}`;
    };

    const onChange = (event, selectedDate) => {
        const currentDate = selectedDate;
        setShow(false);
        setDate(currentDate);
    };

    const showMode = (currentMode) => {
        setShow(true);
        setMode(currentMode);
    };

    const showDatepicker = () => {
        showMode('date');
    };

    const showTimepicker = () => {
        showMode('time');
    };

    const resetTaskModal = useCallback(() => {
        setIsCreatingTask(false);
        setModalVisible(false);
        setNewTaskText('');
        setNewTaskTitle('');
        setSelectedTask(null);
        setSelectedFiles([]);
        setExistingTaskFiles([]);
        setIsTaskReport(false);
        setSelectedReportingServices({});
        setAnotherService('');
        setAnotherServiceChecked({ need_do: false, need_submit: false });
    }, []);

    const toggleModal = () => {
        if (!isModalVisible && isServiceSuspended) return;
        setIsCreatingTask(false);
        setNewTaskText('')
        setNewTaskTitle('')
        setSelectedTask(null)
        setModalVisible(!isModalVisible);
        setSelectedFiles([]);
        setExistingTaskFiles([]);
        setIsTaskReport(false);
        setSelectedReportingServices({});
        setAnotherService('');
        setAnotherServiceChecked({ need_do: false, need_submit: false });

        // Загружаем список reporting_services при открытии модального окна для новой задачи
        if (!isModalVisible && !selectedTask) {
            getReportingServices()
                .then(data => {
                    setReportingServices(data);
                })
                .catch(error => {
                    console.error('Error fetching reporting services:', error);
                });
        }
    };

    const handleTaskPress = (selectedTask) => {
        setSelectedTask(selectedTask); // Устанавливаем выбранную задачу в состояние

        setNewTaskText(selectedTask.text); // Устанавливаем текст задачи
        setNewTaskTitle(selectedTask.title || '');
        const taskDate = new Date(selectedTask.timeout);
        setDate(taskDate); // Устанавливаем таймаут задачи
        // Устанавливаем время из задачи
        const hours = String(taskDate.getHours()).padStart(2, '0');
        const minutes = String(taskDate.getMinutes()).padStart(2, '0');
        setTime(`${hours}:${minutes}`);
        setExistingTaskFiles(selectedTask.files || []); // Устанавливаем существующие файлы задачи
        setSelectedFiles([]); // Очищаем выбранные файлы

        setModalVisible(true); // Открываем модальное окно
    };


    const handleFileSelect = (event) => {
        const files = Array.from(event.target.files);
        const currentCount = selectedFiles.length;
        const remainingSlots = Math.max(0, MAX_TASK_FILES - existingTaskFiles.length - currentCount);

        if (remainingSlots > 0) {
            const filesToAdd = files.slice(0, remainingSlots);
            setSelectedFiles(prevFiles => [...prevFiles, ...filesToAdd]);
        }
        // Сбрасываем значение input, чтобы можно было выбрать тот же файл снова
        event.target.value = '';
    };

    const removeSelectedFile = (index) => {
        setSelectedFiles(prevFiles => prevFiles.filter((_, i) => i !== index));
    };

    useEffect(() => {
        let cancelled = false;
        const revokeUrls = (items) => {
            (items || []).forEach((item) => {
                if (item?.url) URL.revokeObjectURL(item.url);
            });
        };

        if (!selectedFiles.length) {
            setUploadPreviews((prev) => {
                revokeUrls(prev);
                return [];
            });
            return;
        }

        (async () => {
            const next = await Promise.all(
                selectedFiles.map(async (file) => {
                    try {
                        return await buildAttachmentPreview(file);
                    } catch {
                        return { kind: 'name', text: String(file?.name || 'Файл'), name: String(file?.name || 'Файл') };
                    }
                })
            );
            if (cancelled) {
                revokeUrls(next);
                return;
            }
            setUploadPreviews((prev) => {
                revokeUrls(prev);
                return next;
            });
        })();

        return () => { cancelled = true; };
    }, [selectedFiles]);

    useEffect(() => () => {
        uploadPreviews.forEach((item) => {
            if (item?.url) URL.revokeObjectURL(item.url);
        });
    }, [uploadPreviews]);

    const openLocalFile = (file) => {
        const objectUrl = URL.createObjectURL(file);
        window.open(objectUrl, '_blank', 'noopener,noreferrer');
        setTimeout(() => URL.revokeObjectURL(objectUrl), 60000);
        setFileActionModal(null);
    };

    const saveLocalFile = (file) => {
        const objectUrl = URL.createObjectURL(file);
        const link = document.createElement('a');
        link.href = objectUrl;
        link.download = file?.name || 'file';
        link.click();
        URL.revokeObjectURL(objectUrl);
        setFileActionModal(null);
    };

    const handleCreateTask = () => {
        if (isCreatingTask || isServiceSuspended) return;
        let reporting_services = null;

        // Если это задача по отчетности, формируем массив reporting_services
        // Добавляем только те, где выбран чекбокс "Сделать"
        if (isTaskReport) {
            reporting_services = Object.keys(selectedReportingServices)
                .filter(id => selectedReportingServices[id].need_do)
                .map(id => ({
                    id: parseInt(id),
                    need_submit: selectedReportingServices[id].need_submit || false
                }));

            // Если выбран пункт "Другие" и заполнено поле, добавляем как элемент массива с id: 0
            if (anotherServiceChecked.need_do && anotherService.trim()) {
                reporting_services.push({
                    id: 0,
                    need_submit: anotherServiceChecked.need_submit || false,
                    another_service: anotherService.trim()
                });
            }
        }

        // Объединяем дату и время (только если не задача по отчетности)
        let timeout = null;
        if (!isTaskReport) {
            const dateTime = new Date(date);
            const [hours, minutes] = time.split(':');
            dateTime.setHours(parseInt(hours, 10));
            dateTime.setMinutes(parseInt(minutes, 10));
            timeout = formatLocalDateTime(dateTime);
        }

        const finishCreating = () => setIsCreatingTask(false);

        // Проверяем есть ли файлы
        if (selectedFiles.length > 0) {
            setIsCreatingTask(true);
            createTaskWithFiles(taskOwnerUserId, newTaskTitle, isTaskReport ? null : newTaskText, {
                timeout: timeout,
                urgency: null,
                company_id: taskCompanyId,
            }, selectedFiles, data => {
                console.log('createTaskWithFiles', data)
                if (!data || data.error || data.code === 'buh_service_suspended' || !data.id) {
                    alert(data?.error || 'Не удалось создать задачу');
                    return;
                }
                setTasks([data, ...tasks]);
                setNewTaskText('');
                setNewTaskTitle('');
                toggleModal();
            }, finishCreating);
        } else {
            setIsCreatingTask(true);
            createTask(taskOwnerUserId, newTaskTitle, isTaskReport ? null : newTaskText, {
                timeout: timeout,
                urgency: null,
                is_task_report: isTaskReport,
                reporting_services: reporting_services,
                company_id: taskCompanyId,
            }, data => {
                console.log('createTask', data)
                if (!data || data.error || data.code === 'buh_service_suspended' || !data.id) {
                    alert(data?.error || 'Не удалось создать задачу');
                    return;
                }
                // Если задача по отчетности, перезагружаем список задач для получения отчетов
                if (isTaskReport) {
                    return getTasks(taskOwnerUserId, { company_id: taskCompanyId })
                        .then(tasksData => {
                            applyCompanyTasks(tasksData);
                            setNewTaskText('');
                            setNewTaskTitle('');
                            toggleModal();
                        })
                        .catch(error => {
                            console.error('Error fetching tasks after creation:', error);
                            setNewTaskText('');
                            setNewTaskTitle('');
                            toggleModal();
                        });
                }
                setTasks([data, ...tasks]);
                setNewTaskText('');
                setNewTaskTitle('');
                toggleModal();
            }, finishCreating);
        }
    };

    const handleReportingServiceChange = (serviceId, field, checked) => {
        setSelectedReportingServices(prev => {
            const newState = {
                ...prev,
                [serviceId]: {
                    ...prev[serviceId],
                    [field]: checked
                }
            };

            // Если выбираем need_submit, автоматически устанавливаем need_do
            if (field === 'need_submit' && checked) {
                newState[serviceId].need_do = true;
            }

            return newState;
        });
    };

    const handleUpdateTask = () => {
        if (!selectedTask) {
            return; // Если задача не выбрана, ничего не делаем
        }

        // Объединяем дату и время
        const dateTime = new Date(date);
        const [hours, minutes] = time.split(':');
        dateTime.setHours(parseInt(hours, 10));
        dateTime.setMinutes(parseInt(minutes, 10));

        // Передаем локальное "настенное" время без UTC-смещения.
        const timeout = formatLocalDateTime(dateTime);

        // Проверяем есть ли новые файлы
        if (selectedFiles.length > 0) {
            updateTaskWithFiles(selectedTask.id, newTaskTitle, newTaskText, {
                timeout: timeout,
                urgency: null
            }, selectedFiles, data => {
                console.log('updateTaskWithFiles', data);
                // Перезапрашиваем все задачи с сервера
                getTasks(taskOwnerUserId, { company_id: taskCompanyId })
                    .then(data => {
                        applyCompanyTasks(data);
                        setNewTaskText('');
                        setNewTaskTitle('');
                        toggleModal();
                    })
                    .catch(error => {
                        console.error('Error fetching tasks:', error);
                    });
            });
        } else {
            updateTask(selectedTask.id, newTaskTitle, newTaskText, {
                timeout: timeout,
                urgency: null
            }, data => {
                console.log('updateTask', data);
                // Перезапрашиваем все задачи с сервера
                getTasks(taskOwnerUserId, { company_id: taskCompanyId })
                    .then(data => {
                        applyCompanyTasks(data);
                        setNewTaskText('');
                        setNewTaskTitle('');
                        toggleModal();
                    })
                    .catch(error => {
                        console.error('Error fetching tasks:', error);
                    });
            });
        }
    };

    const handleLogOut = () => {
        setIsLoggedIn(false)
        dispatch(setIsLogOut(true))
    }

    const handleAccountSelect = (user) => {
        resetTaskModal();
        selectAccount(user);
    };

    const handleOpenPicker = () => {
        resetTaskModal();
        openPicker();
    };

    const viewerUserId = businessAccountId || user_id;
    const readyUnreadCount = taskCompanyId
        ? Number(tasksByCompanyId?.[taskCompanyId]) || 0
        : Number(totals?.tasks) || 0;
    const readyBadge = formatBadgeCount(readyUnreadCount);

    const handleFilterClick = async (filter) => {
        setSelectedFilter(filter);
        if (filter !== 'ready' || !taskOwnerUserId) return;
        try {
            const data = await getTasks(taskOwnerUserId, { company_id: taskCompanyId });
            applyCompanyTasks(data);
            await markTasksViewed(viewerUserId, { company_id: taskCompanyId });
            refreshAccountBadges();
        } catch (e) {
            console.warn('markTasksViewed failed', e);
        }
    };

    useEffect(() => {
        if (user_id === 0) navigate('/')
        if (pickerOpen || noAccess || accessLoading) {
            setTasks([]);
            return;
        }
        if (!taskOwnerUserId) return;
        resetTaskModal();
        getTasks(taskOwnerUserId, { company_id: taskCompanyId })
            .then(data => {
                console.log(data)
                applyCompanyTasks(data);
            })
            .catch(error => {
                console.error('Error fetching tasks:', error);
            });
    }, [user_id, taskOwnerUserId, taskCompanyId, navigate, resetTaskModal, pickerOpen, noAccess, accessLoading, applyCompanyTasks]);

    useEffect(() => {
        if (pickerOpen) {
            resetTaskModal();
        }
    }, [pickerOpen, resetTaskModal]);

    const visibleTasks = useMemo(
        () => tasks.filter((task) => isTaskVisibleByFilter(task)),
        [tasks, selectedFilter]
    );

    const searchedTasks = useMemo(() => {
        const q = taskSearchQuery.trim().toLowerCase();
        if (!isSearchMode || !q) return visibleTasks;
        return visibleTasks.filter((task) => buildTaskSearchHaystack(task).includes(q));
    }, [visibleTasks, isSearchMode, taskSearchQuery]);

    useEffect(() => {
        const q = taskSearchQuery.trim().toLowerCase();
        if (!isSearchMode || !q) {
            setSearchMatchIndices([]);
            setActiveSearchMatchIdx(0);
            return;
        }
        const matches = [];
        visibleTasks.forEach((task, idx) => {
            if (buildTaskSearchHaystack(task).includes(q)) matches.push(idx);
        });
        setSearchMatchIndices(matches);
        setActiveSearchMatchIdx(0);
    }, [visibleTasks, isSearchMode, taskSearchQuery]);

    const scrollToTaskMatch = (matchArrayIndex) => {
        const taskIndex = searchMatchIndices[matchArrayIndex];
        if (typeof taskIndex !== 'number') return;
        const el = document.getElementById(`task-card-${taskIndex}`);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    };

    useEffect(() => {
        if (!isSearchMode || searchMatchIndices.length === 0) return;
        scrollToTaskMatch(activeSearchMatchIdx);
    }, [activeSearchMatchIdx, isSearchMode, searchMatchIndices]);

    const jumpSearchMatch = (direction) => {
        if (!searchMatchIndices.length) return;
        setActiveSearchMatchIdx((prev) => {
            if (direction === 'prev') return prev <= 0 ? searchMatchIndices.length - 1 : prev - 1;
            return prev >= searchMatchIndices.length - 1 ? 0 : prev + 1;
        });
    };

    const enterSearchMode = () => setIsSearchMode(true);
    const exitSearchMode = () => {
        setIsSearchMode(false);
        setTaskSearchQuery('');
        setSearchMatchIndices([]);
        setActiveSearchMatchIdx(0);
    };

    useEffect(() => {
        if (taskContextMenu) {
            const closeMenu = () => setTaskContextMenu(null);
            document.addEventListener('click', closeMenu);
            return () => document.removeEventListener('click', closeMenu);
        }
    }, [taskContextMenu]);
    return (
        <div className={`tasks_cntr section-page tasks-page${pickerOpen ? ' section-page--picker-open' : ''}`}>

            <div className="section-page-heading manrope-bold color_white">Задачи</div>
            {!pickerOpen && (
            <div className='page_title_cntr section-page_title_cntr'>
                {multiple && (
                    <button
                        type="button"
                        className="page-header-nav-back"
                        onClick={handleOpenPicker}
                        aria-label="К списку компаний"
                    >
                        {arrowLIcon}
                    </button>
                )}
                <div className="manrope-bold color_white section-company-title">{name}</div>
                <div
                    className="manrope-bold color_white page_title2 section-page_actions"
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                    }}
                >
                    {isSearchMode ? (
                        <>
                            <input
                                value={taskSearchQuery}
                                onChange={(e) => setTaskSearchQuery(e.target.value)}
                                placeholder='Поиск по задачам'
                                autoFocus
                                style={{
                                    height: '30px',
                                    borderRadius: '8px',
                                    border: 'none',
                                    outline: 'none',
                                    padding: '0 10px',
                                    minWidth: '180px',
                                    maxWidth: '28vw',
                                    fontFamily: 'Manrope-Bold, sans-serif',
                                    fontSize: '13px',
                                }}
                            />
                            <button
                                type='button'
                                onClick={() => jumpSearchMatch('prev')}
                                disabled={searchMatchIndices.length === 0}
                                style={{
                                    width: '24px',
                                    height: '24px',
                                    borderRadius: '6px',
                                    border: '1px solid rgba(255,255,255,0.45)',
                                    background: 'rgba(255,255,255,0.2)',
                                    color: '#fff',
                                    cursor: searchMatchIndices.length ? 'pointer' : 'not-allowed',
                                    opacity: searchMatchIndices.length ? 1 : 0.45,
                                    padding: 0,
                                }}
                                aria-label='Предыдущее совпадение'
                            >
                                &#9650;
                            </button>
                            <button
                                type='button'
                                onClick={() => jumpSearchMatch('next')}
                                disabled={searchMatchIndices.length === 0}
                                style={{
                                    width: '24px',
                                    height: '24px',
                                    borderRadius: '6px',
                                    border: '1px solid rgba(255,255,255,0.45)',
                                    background: 'rgba(255,255,255,0.2)',
                                    color: '#fff',
                                    cursor: searchMatchIndices.length ? 'pointer' : 'not-allowed',
                                    opacity: searchMatchIndices.length ? 1 : 0.45,
                                    padding: 0,
                                }}
                                aria-label='Следующее совпадение'
                            >
                                &#9660;
                            </button>
                            <button
                                type='button'
                                onClick={exitSearchMode}
                                style={{
                                    width: '24px',
                                    height: '24px',
                                    borderRadius: '6px',
                                    border: '1px solid rgba(255,255,255,0.45)',
                                    background: 'rgba(255,255,255,0.2)',
                                    color: '#fff',
                                    cursor: 'pointer',
                                    padding: 0,
                                }}
                                aria-label='Закрыть поиск'
                            >
                                &#10005;
                            </button>
                        </>
                    ) : (
                        <>
                            <button
                                type='button'
                                onClick={enterSearchMode}
                                style={{
                                    width: '28px',
                                    height: '28px',
                                    borderRadius: '14px',
                                    border: '1px solid rgba(255,255,255,0.45)',
                                    background: 'rgba(255,255,255,0.2)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: 'pointer',
                                    padding: 0,
                                }}
                                aria-label='Поиск'
                            >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
                                    <path d="M11 4a7 7 0 1 0 4.4 12.44l3.58 3.58a1 1 0 0 0 1.42-1.42l-3.58-3.58A7 7 0 0 0 11 4Zm0 2a5 5 0 1 1 0 10 5 5 0 0 1 0-10Z" fill="#FFFFFF" />
                                </svg>
                            </button>
                        </>
                    )}
                </div>
            </div>
            )}
            <div className={`tasks_subcntr${pickerOpen ? ' section-page-picker' : ''}`}>
                {pickerOpen ? (
                    accessLoading ? (
                        <div className="profile-empty manrope-bold" style={{ padding: '40px 16px', textAlign: 'center', color: '#64748b' }}>
                            Загрузка…
                        </div>
                    ) : accessLoadError ? (
                        <div className="profile-empty manrope-bold" style={{ padding: '40px 16px', textAlign: 'center', color: '#64748b' }}>
                            {accessLoadError}
                        </div>
                    ) : noAccess ? (
                        <div className="profile-empty manrope-bold" style={{ padding: '40px 16px', textAlign: 'center', color: '#64748b' }}>
                            Вам не предоставлен доступ
                        </div>
                    ) : (
                        <CompanyPickerList
                            users={pickerUsers}
                            onSelect={handleAccountSelect}
                            badgeByUserId={isEmployee ? null : pickerBadgeByUserId}
                        />
                    )
                ) : (
                <>
                <div className='task_filter'>
                    <div
                        className={`task_filter_btn ${selectedFilter === 'inProgress' ? 'task_filter_btn' : 'task_filter_btn_d'}`}
                        onClick={() => handleFilterClick('inProgress')}
                    >
                        В работе
                    </div>
                    <div
                        className={`task_filter_btn ${selectedFilter === 'ready' ? 'task_filter_btn' : 'task_filter_btn_d'}`}
                        onClick={() => handleFilterClick('ready')}
                    >
                        Готовые
                        {readyBadge ? (
                            <span className="task_filter_badge">{readyBadge}</span>
                        ) : null}
                    </div>
                </div>
                {/* Список задач */}
                <div className='cntr_tasks'>
                    {searchedTasks.map((item) => (
                        <div
                            key={item.id}
                            id={`task-card-${visibleTasks.findIndex((t) => t.id === item.id)}`}
                            onContextMenu={(e) => {
                                e.preventDefault();
                                setTaskContextMenu({
                                    x: e.clientX,
                                    y: e.clientY,
                                    text: `${item.title || ''}\n${item.text || ''}\n${item.comment || ''}`.trim(),
                                });
                            }}
                        >
                            <TaskItem
                                task={item}
                                selectedFilter={selectedFilter}
                                onTaskClick={handleTaskPress}
                            />
                        </div>
                    ))}
                </div>
                {taskContextMenu && (
                    <div
                        style={{
                            position: 'fixed',
                            left: taskContextMenu.x,
                            top: taskContextMenu.y,
                            zIndex: 10000,
                            backgroundColor: '#fff',
                            border: '1px solid #ddd',
                            borderRadius: '8px',
                            boxShadow: '0 2px 10px rgba(0,0,0,0.15)',
                            padding: '4px 0',
                            minWidth: '160px',
                        }}
                    >
                        <div
                            style={{ padding: '10px 16px', cursor: 'pointer', fontSize: '14px' }}
                            onMouseEnter={(e) => (e.target.style.backgroundColor = '#f0f0f0')}
                            onMouseLeave={(e) => (e.target.style.backgroundColor = 'transparent')}
                            onClick={async () => {
                                if (taskContextMenu.text) {
                                    try {
                                        await navigator.clipboard.writeText(taskContextMenu.text);
                                    } catch (err) {
                                        console.warn('clipboard write failed', err);
                                    }
                                }
                                setTaskContextMenu(null);
                            }}
                        >
                            Копировать
                        </div>
                    </div>
                )}
                {fileActionModal && (
                    <div
                        style={{
                            position: 'fixed',
                            inset: 0,
                            backgroundColor: 'rgba(0,0,0,0.45)',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            zIndex: 10020,
                        }}
                        onClick={() => setFileActionModal(null)}
                    >
                        <div
                            style={{
                                backgroundColor: '#fff',
                                borderRadius: '12px',
                                padding: '18px',
                                minWidth: '280px',
                                maxWidth: '90vw',
                            }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div style={{ fontSize: '14px', color: '#111827', marginBottom: '12px', wordBreak: 'break-word' }}>
                                {fileActionModal.name || 'Файл'}
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                <button type='button' className='create-button manrope-bold' style={{ marginTop: 0 }} onClick={() => openLocalFile(fileActionModal)}>
                                    Открыть
                                </button>
                                <button type='button' className='create-button manrope-bold' style={{ marginTop: 0 }} onClick={() => saveLocalFile(fileActionModal)}>
                                    Сохранить
                                </button>
                                <button type='button' className='create-button manrope-bold' style={{ marginTop: 0, backgroundColor: '#94A3B8' }} onClick={() => setFileActionModal(null)}>
                                    Отмена
                                </button>
                            </div>
                        </div>
                    </div>
                )}
                {/* Модальное окно для создания/редактирования задачи */}
                {isModalVisible && (
                    <div className="modal-container">
                        <div className="modal-content">
                            <div className="close-button" onClick={toggleModal}>
                                {closeIconTask}
                            </div>
                            <div 
                                style={{ 
                                    display: 'flex', 
                                    justifyContent: 'space-between', 
                                    alignItems: 'center', 
                                    // marginBottom: '20px' 
                                    }}>
                                <div style={{ textAlign: 'center', flex: 1 }} className='manrope-bold color_blue_1 fs-20'>
                                    {selectedTask ? 'Задача' : 'Новая задача'}
                                </div>
                                {!selectedTask && (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                        <span className='manrope-bold' style={{ fontSize: '14px' }}>Отчетность</span>
                                        <div
                                            className={`toggle-switch ${isTaskReport ? 'active' : ''}`}
                                            onClick={() => setIsTaskReport(!isTaskReport)}
                                        >
                                            <div className="toggle-switch-slider"></div>
                                        </div>
                                    </div>
                                )}
                            </div>
                            {isTaskReport && <label>*Тем, кто не находится на постоянном обслуживании</label>}
                            <div className="modal-task-body">
                                {!isTaskReport ? (
                                    <>
                                        <div>
                                            <label className='manrope-bold'>Заголовок</label>
                                            <input
                                                style={{ marginTop: '10px' }}
                                                className="input_task "
                                                type="text"
                                                placeholder="Заголовок задачи"
                                                value={newTaskTitle}
                                                onChange={(e) => setNewTaskTitle(e.target.value)}
                                                disabled={!!selectedTask?.finish_date}
                                                maxLength={56}
                                            />
                                        </div>
                                        <div>
                                            <label className='manrope-bold'>Текст задачи</label>
                                            <textarea
                                                style={{
                                                    marginTop: '10px',
                                                    minHeight: '56px',
                                                    height: '56px',
                                                    resize: 'none',
                                                    overflow: 'auto',
                                                    paddingTop: '10px',
                                                    paddingBottom: '10px',
                                                }}
                                                className="input_task "
                                                placeholder="Текст задачи"
                                                value={newTaskText}
                                                onChange={(e) => setNewTaskText(e.target.value)}
                                                onInput={(e) => {
                                                    e.target.style.height = '56px';
                                                    e.target.style.height = `${Math.min(e.target.scrollHeight, 140)}px`;
                                                }}
                                                disabled={!!selectedTask?.finish_date}
                                            />
                                            {selectedTask?.comment && (
                                                <div style={{ marginTop: '8px', textAlign: 'left', minWidth: 0 }}>
                                                    <div style={{ fontSize: '12px', color: '#64748B' }}>Бухгалтер:</div>
                                                    <div
                                                        style={{
                                                            fontSize: '12px',
                                                            color: '#64748B',
                                                            marginTop: '2px',
                                                            wordBreak: 'break-word',
                                                            overflowWrap: 'break-word',
                                                            whiteSpace: 'pre-wrap',
                                                            maxWidth: '100%',
                                                        }}
                                                    >
                                                        {selectedTask.comment}
                                                    </div>
                                                </div>
                                            )}
                                        </div>

                                        {/* Существующие файлы: пользователь, затем от бухгелтерии */}
                                        {existingTaskFiles.length > 0 && (() => {
                                            const userFiles = existingTaskFiles.filter(f => !f.from_accountant);
                                            const accountantFiles = existingTaskFiles.filter(f => f.from_accountant);
                                            return (
                                                <div style={{ marginTop: '10px' }}>
                                                    {userFiles.length > 0 && (
                                                        <>
                                                            <label className='manrope-bold' style={{ fontSize: '14px', color: '#94A3B8', marginBottom: '10px', display: 'block' }}>Текущие файлы:</label>
                                                            <div style={{ display: 'flex', flexWrap: 'wrap', marginBottom: '10px', gap: '8px' }}>
                                                                {userFiles.filter(f => f.path && f.path.match(/\.(jpg|jpeg|png|gif)$/i)).map((file, index) => (
                                                                    <a key={`user-img-${index}`} href={`${url}/${file.path}`} target="_blank" rel="noopener noreferrer" style={{ cursor: 'pointer' }}>
                                                                        <img src={`${url}/${file.path}`} alt="" style={{ width: '40px', height: '40px', borderRadius: '5px', objectFit: 'cover' }} />
                                                                    </a>
                                                                ))}
                                                            </div>
                                                            {userFiles.filter(f => !f.path || !f.path.match(/\.(jpg|jpeg|png|gif)$/i)).map((file, index) => (
                                                                <div key={`user-file-${index}`} style={{ marginBottom: '5px' }}>
                                                                    <FileMessage path={file.path} size={file.size} fileAction='choice' />
                                                                </div>
                                                            ))}
                                                        </>
                                                    )}
                                                    {accountantFiles.length > 0 && (
                                                        <>
                                                            <label className='manrope-bold' style={{ fontSize: '12px', color: '#1565C0', marginTop: '12px', marginBottom: '6px', display: 'block' }}>От бухгалтера</label>
                                                            <div style={{ display: 'flex', flexWrap: 'wrap', marginBottom: '10px', gap: '8px' }}>
                                                                {accountantFiles.filter(f => f.path && f.path.match(/\.(jpg|jpeg|png|gif)$/i)).map((file, index) => (
                                                                    <a key={`acc-img-${index}`} href={`${url}/${file.path}`} target="_blank" rel="noopener noreferrer" style={{ cursor: 'pointer', padding: '4px', borderRadius: '7px', border: '2px solid #1565C0' }}>
                                                                        <img src={`${url}/${file.path}`} alt="" style={{ width: '40px', height: '40px', borderRadius: '5px', objectFit: 'cover' }} />
                                                                    </a>
                                                                ))}
                                                            </div>
                                                            {accountantFiles.filter(f => !f.path || !f.path.match(/\.(jpg|jpeg|png|gif)$/i)).map((file, index) => (
                                                                <div key={`acc-file-${index}`} style={{ marginBottom: '5px', backgroundColor: '#E3F2FD', padding: '6px', borderRadius: '6px' }}>
                                                                    <FileMessage path={file.path} size={file.size} fileAction='choice' />
                                                                </div>
                                                            ))}
                                                        </>
                                                    )}
                                                </div>
                                            );
                                        })()}

                                        {/* Добавление файлов */}
                                        <div style={{ width: '100%', marginTop: '5px', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                                            <label className='manrope-bold' style={{ fontSize: '16' }}>Добавить файл</label>
                                            <label
                                                style={{
                                                    cursor: (existingTaskFiles.length + selectedFiles.length) >= MAX_TASK_FILES || !!selectedTask?.finish_date ? 'not-allowed' : 'pointer',
                                                    opacity: (existingTaskFiles.length + selectedFiles.length) >= MAX_TASK_FILES || !!selectedTask?.finish_date ? 0.5 : 1,
                                                    display: 'flex',
                                                    alignItems: 'center'
                                                }}
                                            >
                                                {fileSelect}
                                                <input
                                                    type="file"
                                                    multiple
                                                    onChange={handleFileSelect}
                                                    style={{ display: 'none' }}
                                                    disabled={(existingTaskFiles.length + selectedFiles.length) >= MAX_TASK_FILES || !!selectedTask?.finish_date}
                                                />
                                            </label>
                                        </div>

                                        {/* Выбранные файлы */}
                                        {selectedFiles.length > 0 && (
                                            <div style={{ marginTop: '10px' }}>
                                                {selectedFiles.map((file, originalIndex) => {
                                                    const p = uploadPreviews[originalIndex];
                                                    const rawName = String(file?.name || 'Файл');
                                                    const shortName = rawName.length > 40 ? `${rawName.slice(0, 40)}...` : rawName;
                                                    return (
                                                        <div
                                                            key={`${rawName}-${originalIndex}`}
                                                            onClick={() => setFileActionModal(file)}
                                                            style={{
                                                                display: 'flex',
                                                                alignItems: 'flex-start',
                                                                gap: '8px',
                                                                marginBottom: '8px',
                                                                cursor: 'pointer',
                                                                backgroundColor: '#fff',
                                                                border: '1px solid #e5e7eb',
                                                                borderRadius: '8px',
                                                                padding: '6px',
                                                            }}
                                                        >
                                                            {!selectedTask?.finish_date && (
                                                                <button
                                                                    type='button'
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        removeSelectedFile(originalIndex);
                                                                    }}
                                                                    style={{
                                                                        border: 'none',
                                                                        background: '#ef4444',
                                                                        color: '#fff',
                                                                        width: '18px',
                                                                        height: '18px',
                                                                        borderRadius: '50%',
                                                                        cursor: 'pointer',
                                                                        fontSize: '12px',
                                                                        lineHeight: '18px',
                                                                        padding: 0,
                                                                        flexShrink: 0,
                                                                    }}
                                                                    aria-label='Удалить файл'
                                                                    title='Удалить файл'
                                                                >
                                                                    ×
                                                                </button>
                                                            )}
                                                            {p?.kind === 'image' && p?.url ? (
                                                                <img
                                                                    src={p.url}
                                                                    alt=''
                                                                    style={{ width: '36px', height: '36px', borderRadius: '6px', objectFit: 'cover' }}
                                                                />
                                                            ) : (
                                                                <div style={{ width: '24px', height: '24px', borderRadius: '6px', background: '#E5E7EB' }} />
                                                            )}
                                                            <div style={{ minWidth: 0 }}>
                                                                <div style={{ color: '#111827', wordBreak: 'break-word', fontSize: '13px' }}>{shortName}</div>
                                                                {p?.kind === 'text' && (
                                                                    <div style={{ color: '#6b7280', marginTop: '4px', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                                                        {(p.text || '').slice(0, 160)}
                                                                        {(p.text || '').length > 160 ? '...' : ''}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}

                                        {(existingTaskFiles.length + selectedFiles.length) >= MAX_TASK_FILES && (
                                            <div style={{ fontSize: '12px', color: '#FF6B6B', marginTop: '5px' }}>
                                                Достигнут лимит в {MAX_TASK_FILES} файлов
                                            </div>
                                        )}
                                        <div className='d-f' style={{ justifyContent: 'space-between', gap: '2px' }}>
                                            <div>
                                                <div className='manrope-bold' style={{ marginBottom: '10px' }}>
                                                    К какой дате выполнить
                                                </div>
                                                <DatePicker onChange={setDate} value={date} className='datepicker'
                                                    disabled={!!selectedTask}
                                                />
                                            </div>
                                            <div>
                                                <div className='manrope-bold' style={{ marginBottom: '10px' }}>
                                                    К какому времени выполнить
                                                </div>
                                                <TimePicker onChange={setTime} value={time} className='datepicker'
                                                    disabled={!!selectedTask}
                                                />
                                            </div>
                                        </div>
                                        {/* <div className="date-details">
                                <span>Таймаут:</span> */}
                                        {/* <button className="date-button" onClick={showDatepicker}>
                                    {date?.toLocaleString().slice(0, 10)}
                                </button> */}
                                        {/* <button className="date-button" onClick={showTimepicker}>
                                    {date?.toLocaleString().slice(12, 17)}
                                </button> */}
                                        {/* <input aria-label="Time" type="time" /> */}
                                        {/* <TimePicker onChange={setTime} value={time} className='datepicker' />
                                {true && (
                                    <DatePicker onChange={setDate} value={date} className='datepicker' />
                                )}
                            </div> */}
                                    </>
                                ) : (
                                    <div style={{ marginTop: '0px', maxHeight: '50vh', overflowY: 'auto', flex: 1 }}>
                                        
                                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                            <thead>
                                                <tr style={{ borderBottom: '2px solid #E2E8F0' }}>
                                                    <th style={{ textAlign: 'left', padding: '8px', fontWeight: 'bold', fontSize: '14px' }} className='manrope-bold'>Наименование</th>
                                                    <th style={{ textAlign: 'center', padding: '8px', fontWeight: 'bold', fontSize: '14px' }} className='manrope-bold'>Сделать</th>
                                                    <th style={{ textAlign: 'center', padding: '8px', fontWeight: 'bold', fontSize: '14px' }} className='manrope-bold'>Сдать</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {reportingServices.map((service) => (
                                                    <tr key={service.id} style={{ borderBottom: '1px solid #E2E8F0' }}>
                                                        <td style={{ padding: '4px 8px', fontSize: '14px' }}>{service.name}</td>
                                                        <td style={{ textAlign: 'center', padding: '6px 8px' }}>
                                                            <input
                                                                type='checkbox'
                                                                checked={selectedReportingServices[service.id]?.need_do || false}
                                                                onChange={(e) => handleReportingServiceChange(service.id, 'need_do', e.target.checked)}
                                                            />
                                                        </td>
                                                        <td style={{ textAlign: 'center', padding: '6px 8px' }}>
                                                            <input
                                                                type='checkbox'
                                                                checked={selectedReportingServices[service.id]?.need_submit || false}
                                                                onChange={(e) => handleReportingServiceChange(service.id, 'need_submit', e.target.checked)}
                                                            />
                                                        </td>
                                                    </tr>
                                                ))}
                                                {/* Пункт "Другие" */}
                                                <tr style={{ borderBottom: '1px solid #E2E8F0' }}>
                                                    <td style={{ padding: '6px 8px', fontSize: '14px' }}>
                                                        <input
                                                            type='text'
                                                            placeholder='Другие'
                                                            value={anotherService}
                                                            onChange={(e) => setAnotherService(e.target.value)}
                                                            style={{
                                                                width: '100%',
                                                                padding: '4px 8px',
                                                                border: '1px solid #E2E8F0',
                                                                borderRadius: '4px',
                                                                fontSize: '14px',
                                                                outline: 'none'
                                                            }}
                                                        />
                                                    </td>
                                                    <td style={{ textAlign: 'center', padding: '6px 8px' }}>
                                                        <input
                                                            type='checkbox'
                                                            checked={anotherServiceChecked.need_do}
                                                            onChange={(e) => {
                                                                setAnotherServiceChecked(prev => ({
                                                                    ...prev,
                                                                    need_do: e.target.checked,
                                                                    need_submit: e.target.checked && prev.need_submit ? prev.need_submit : false
                                                                }));
                                                            }}
                                                        />
                                                    </td>
                                                    <td style={{ textAlign: 'center', padding: '6px 8px' }}>
                                                        <input
                                                            type='checkbox'
                                                            checked={anotherServiceChecked.need_submit}
                                                            onChange={(e) => {
                                                                setAnotherServiceChecked(prev => ({
                                                                    ...prev,
                                                                    need_submit: e.target.checked,
                                                                    need_do: e.target.checked ? true : prev.need_do
                                                                }));
                                                            }}
                                                        />
                                                    </td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                            <div className="modal-task-footer">
                                {selectedTask ? (
                                    <button className="create-button manrope-bold" onClick={handleUpdateTask}>
                                        СОХРАНИТЬ
                                    </button>
                                ) : (
                                    <button
                                        type="button"
                                        className="create-button manrope-bold"
                                        onClick={handleCreateTask}
                                        disabled={isCreatingTask || isServiceSuspended}
                                    >
                                        {isCreatingTask ? 'Загрузка' : 'СОЗДАТЬ'}
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                )}
                </>
                )}
            </div>

            {!pickerOpen && !noAccess && (
                <SuspendHoverHint
                    suspended={isServiceSuspended}
                    className="suspend-hover-hint--tasks-fab"
                    alwaysWrap
                >
                <div
                    className={`add_icon${isServiceSuspended ? ' add_icon--suspended' : ''}`}
                    onClick={isServiceSuspended ? undefined : toggleModal}
                    role="button"
                    aria-disabled={isServiceSuspended}
                >
                    {newTask}
                </div>
                </SuspendHoverHint>
            )}

        </div>
    );
}

export default Tasks;

